import React, { useState, useEffect, useRef, useCallback } from 'react';
import api, { getData, postData, putData, deleteData, uploadFile } from '../../../services/api';
import PolicyTab from '../../../components/common/PolicyTab';

// ── constants ──────────────────────────────────────────────────────────────────
const CATEGORIES = [
  { value: 'practice', label: 'Practice',  icon: 'bi-pencil',          color: '#3b82f6', bg: '#eff6ff' },
  { value: 'test',     label: 'Test',      icon: 'bi-clipboard-check', color: '#f59e0b', bg: '#fffbeb' },
  { value: 'exam',     label: 'Exam',      icon: 'bi-mortarboard',     color: '#ef4444', bg: '#fef2f2' },
  { value: 'scholarship', label: 'Scholarship', icon: 'bi-award', color: '#7c3aed', bg: '#f5f3ff' },
];

const RESULT_MODES = [
  { value: 'only_marks',        label: 'Score Only',               icon: 'bi-123',             desc: 'Show total marks scored' },
  { value: 'marks_with_answers',label: 'Score + Correct Answers',  icon: 'bi-check-circle',    desc: 'Show score and which options were correct' },
  { value: 'with_explanation',  label: 'Full Review + Explanation',icon: 'bi-book-open',       desc: 'Show score, answers and explanations' },
  { value: 'after_review',      label: 'Release Later',            icon: 'bi-clock-history',   desc: 'Instructor manually releases results' },
];

const DIFFICULTIES = ['easy', 'medium', 'hard'];
const Q_TYPES = [
  { value: 'mcq',        label: 'Multiple Choice' },
  { value: 'true_false', label: 'True / False' },
  { value: 'fill_blank', label: 'Fill in the Blank' },
];

const DEFAULT_QUIZ = {
  title: '', description: '', thumbnailUrl: '', category: 'practice', subject: '',
  totalDuration: 30, timePerQuestion: 0,
  shuffleQuestions: false, shuffleOptions: false,
  resultDisplay: 'only_marks', passingScore: 40,
  scholarshipConfig: {
    enabled: false, targetCourseId: '', availableFrom: '', availableUntil: '', maxAttempts: 1, maxParticipants: 0,
  },
};

const DEFAULT_QUESTION = {
  text: '', type: 'mcq', difficulty: 'medium', marks: 1, explanation: '',
  options: [
    { text: '', isCorrect: true  },
    { text: '', isCorrect: false },
    { text: '', isCorrect: false },
    { text: '', isCorrect: false },
  ],
};

const prepareQuizQuestions = (rows = []) => rows.map((question) => ({
  ...question,
  text: String(question.text || '').trim(),
  options: (question.options || [])
    .map((option) => ({ ...option, text: String(option.text || '').trim() }))
    .filter((option) => option.text),
}));

const getQuizQuestionError = (rows = []) => {
  for (let index = 0; index < rows.length; index += 1) {
    const question = rows[index];
    if (!question.text) return `Question ${index + 1} needs question text.`;
    if (question.type === 'fill_blank') {
      if (!question.options.length) return `Question ${index + 1} needs the expected answer.`;
      continue;
    }
    if (question.options.length < 2) return `Question ${index + 1} needs at least two options.`;
    if (!question.options.some((option) => option.isCorrect)) {
      return `Question ${index + 1} needs a correct option.`;
    }
  }
  return '';
};

// ── tiny helpers ───────────────────────────────────────────────────────────────
const categoryInfo = (v) => CATEGORIES.find(c => c.value === v) || CATEGORIES[0];

const apiServer = (process.env.REACT_APP_API_URL || 'http://localhost:5030/api').replace(/\/api\/?$/, '');
const quizMediaUrl = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return /^(https?:|data:|blob:)/i.test(raw) ? raw : `${apiServer}${raw.startsWith('/') ? '' : '/'}${raw}`;
};

const formatTime = (secs) => {
  const m = Math.floor(secs / 60).toString().padStart(2, '0');
  const s = (secs % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

const Badge = ({ cat }) => {
  const c = categoryInfo(cat);
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 12, background: c.bg, color: c.color, letterSpacing: 0.4 }}>
      <i className={`bi ${c.icon} me-1`}></i>{c.label.toUpperCase()}
    </span>
  );
};

const ToastNotice = ({ toast }) => {
  if (!toast) return null;
  return (
    <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999 }}>
      <div className={`alert alert-${toast.type === 'danger' ? 'danger' : 'success'} shadow d-flex align-items-center gap-2`} style={{ borderRadius: 12, minWidth: 260 }}>
        <i className={`bi ${toast.type === 'danger' ? 'bi-exclamation-circle' : 'bi-check-circle'}`}></i>
        {toast.message}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
export default function QuizGenerator() {
  // ── top-level view ──────────────────────────────────────────────────────────
  const [view, setView] = useState('list'); // 'list' | 'create' | 'edit' | 'take' | 'result'
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'practice' | 'test' | 'exam' | 'bank'

  // ── quiz list ───────────────────────────────────────────────────────────────
  const [quizzes, setQuizzes]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [toast, setToast]       = useState(null);

  // ── create / edit ───────────────────────────────────────────────────────────
  const [editId, setEditId]         = useState(null);
  const [quizForm, setQuizForm]     = useState(DEFAULT_QUIZ);
  const [questions, setQuestions]   = useState([]);
  const [createStep, setCreateStep] = useState(1); // 1=settings, 2=questions
  const [saveLoading, setSaveLoading] = useState(false);
  const [thumbnailLoading, setThumbnailLoading] = useState(false);
  const [courseOptions, setCourseOptions] = useState([]);

  // ── question source tab ─────────────────────────────────────────────────────
  const [qSource, setQSource] = useState('manual'); // manual | ai | import | bank

  // ── manual question form ────────────────────────────────────────────────────
  const [qForm, setQForm]         = useState(DEFAULT_QUESTION);
  const [editQIdx, setEditQIdx]   = useState(null); // null = new
  const [showQForm, setShowQForm] = useState(false);

  // ── AI generation ───────────────────────────────────────────────────────────
  const [aiForm, setAiForm]     = useState({ topic: '', numQuestions: 10, difficulty: 'medium', questionType: 'mcq' });
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiSelected, setAiSelected] = useState(new Set());

  // ── CSV import ──────────────────────────────────────────────────────────────
  const [csvText, setCsvText] = useState('');
  const [csvParsed, setCsvParsed] = useState([]);
  const [csvSelected, setCsvSelected] = useState(new Set());
  const fileInputRef = useRef(null);

  // ── quiz bank ───────────────────────────────────────────────────────────────
  const [bankQuizzes, setBankQuizzes] = useState([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankSelected, setBankSelected] = useState(new Set()); // selected question ids

  // ── take quiz ───────────────────────────────────────────────────────────────
  const [activeQuiz, setActiveQuiz]       = useState(null);
  const [attempt, setAttempt]             = useState(null);
  const [currentQ, setCurrentQ]           = useState(0);
  const [answers, setAnswers]             = useState({});
  const [timeLeft, setTimeLeft]           = useState(null);
  const [quizResult, setQuizResult]       = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const timerRef = useRef(null);

  // ── load quizzes ────────────────────────────────────────────────────────────
  const loadQuizzes = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeTab !== 'all' && activeTab !== 'bank') params.category = activeTab;
      if (activeTab === 'bank') params.isBank = true;
      if (search) params.search = search;
      const r = await getData('/quiz', params);
      setQuizzes(r.records || r.data || []);
    } catch { setQuizzes([]); }
    setLoading(false);
  }, [activeTab, search]);

  useEffect(() => { loadQuizzes(); }, [loadQuizzes]);

  useEffect(() => {
    getData('/training/courses', { limit: 500 })
      .then((response) => setCourseOptions(response.data || response.records || []))
      .catch(() => setCourseOptions([]));
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const uploadQuizThumbnail = async (file) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      showToast('Use a JPG, PNG, or WebP image.', 'danger');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      showToast('Quiz thumbnail must be under 2 MB.', 'danger');
      return;
    }
    setThumbnailLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await uploadFile('/quiz/upload-thumbnail', formData);
      const url = response?.data?.url || response?.url || '';
      if (!url) throw new Error('Upload completed without a thumbnail URL.');
      setQuizForm((previous) => ({ ...previous, thumbnailUrl: url }));
      showToast('Quiz thumbnail uploaded.');
    } catch (error) {
      showToast(error.response?.data?.message || error.message || 'Thumbnail upload failed.', 'danger');
    } finally {
      setThumbnailLoading(false);
    }
  };

  // ── save quiz ───────────────────────────────────────────────────────────────
  const saveQuiz = async (publish = false) => {
    if (!String(quizForm.title || '').trim()) { showToast('Quiz title is required', 'danger'); return; }
    if (questions.length === 0) {
      const selectedImportCount = csvParsed.filter((_, index) => csvSelected.has(index)).length;
      showToast(
        selectedImportCount > 0
          ? `Click "Add ${selectedImportCount} Selected" before saving the quiz.`
          : 'Add at least one question',
        'danger'
      );
      return;
    }
    if (publish && quizForm.category === 'scholarship' && !quizForm.scholarshipConfig?.targetCourseId) {
      showToast('Select the course that will receive the scholarship discount', 'danger'); return;
    }
    const normalizedQuestions = prepareQuizQuestions(questions);
    const questionError = getQuizQuestionError(normalizedQuestions);
    if (questionError) { showToast(questionError, 'danger'); return; }
    setSaveLoading(true);
    try {
      const { scholarshipConfig, ...quizSettings } = quizForm;
      const payload = {
        ...quizSettings,
        questions: normalizedQuestions,
        status: publish ? 'published' : 'draft',
        ...(quizForm.category === 'scholarship' ? {
          scholarshipConfig: {
            ...scholarshipConfig,
            enabled: true,
            targetCourseId: scholarshipConfig?.targetCourseId || null,
            availableFrom: scholarshipConfig?.availableFrom || null,
            availableUntil: scholarshipConfig?.availableUntil || null,
          },
        } : {}),
      };
      if (editId) {
        await putData(`/quiz/${editId}`, payload);
        showToast(publish ? 'Quiz published!' : 'Quiz updated!');
      } else {
        await postData('/quiz', payload);
        showToast(publish ? 'Quiz published!' : 'Quiz saved as draft!');
      }
      setView('list'); loadQuizzes();
    } catch (e) {
      const responseData = e.response?.data || null;
      console.error('[quiz-save] request failed', {
        status: e.response?.status || null,
        message: responseData?.message || e.message || 'Save failed',
        errors: responseData?.errors || [],
        category: quizForm.category,
        questionCount: normalizedQuestions.length,
        publishing: publish,
      });
      showToast(responseData?.message || 'Save failed', 'danger');
    }
    setSaveLoading(false);
  };

  const deleteQuiz = async (id) => {
    if (!window.confirm('Delete this quiz? All attempts will also be deleted.')) return;
    try { await deleteData(`/quiz/${id}`); loadQuizzes(); showToast('Quiz deleted'); }
    catch { showToast('Delete failed', 'danger'); }
  };

  const saveToBank = async (id) => {
    try { await postData(`/quiz/${id}/save-to-bank`); loadQuizzes(); showToast('Saved to quiz bank!'); }
    catch { showToast('Failed', 'danger'); }
  };

  const publishQuiz = async (id) => {
    try { await postData(`/quiz/${id}/publish`); loadQuizzes(); showToast('Quiz published!'); }
    catch { showToast('Publish failed', 'danger'); }
  };

  // ── open create / edit ──────────────────────────────────────────────────────
  const openCreate = () => {
    setEditId(null); setQuizForm(DEFAULT_QUIZ); setQuestions([]);
    setCreateStep(1); setQSource('manual'); setAiSuggestions([]); setCsvParsed([]); setBankQuizzes([]);
    setView('create');
  };

  const openEdit = async (id) => {
    try {
      const r = await getData(`/quiz/${id}`);
      const q = r.data || r;
      setEditId(id);
      setQuizForm({ title: q.title, description: q.description, thumbnailUrl: q.thumbnailUrl || '', category: q.category, subject: q.subject || '',
        totalDuration: q.totalDuration, timePerQuestion: q.timePerQuestion,
        shuffleQuestions: q.shuffleQuestions, shuffleOptions: q.shuffleOptions,
        resultDisplay: q.resultDisplay, passingScore: q.passingScore,
        scholarshipConfig: {
          enabled: q.category === 'scholarship',
          targetCourseId: q.scholarshipConfig?.targetCourseId?._id || q.scholarshipConfig?.targetCourseId || '',
          availableFrom: q.scholarshipConfig?.availableFrom ? String(q.scholarshipConfig.availableFrom).slice(0, 16) : '',
          availableUntil: q.scholarshipConfig?.availableUntil ? String(q.scholarshipConfig.availableUntil).slice(0, 16) : '',
          maxAttempts: Number(q.scholarshipConfig?.maxAttempts || 1),
          maxParticipants: Number(q.scholarshipConfig?.maxParticipants || 0),
        },
      });
      setQuestions(q.questions || []);
      setCreateStep(1); setQSource('manual'); setAiSuggestions([]); setCsvParsed([]);
      setView('edit');
    } catch { showToast('Failed to load quiz', 'danger'); }
  };

  // ── question management ─────────────────────────────────────────────────────
  const openQForm = (idx = null) => {
    setEditQIdx(idx);
    setQForm(idx !== null ? { ...questions[idx], options: questions[idx].options.map(o => ({...o})) } : { ...DEFAULT_QUESTION, options: DEFAULT_QUESTION.options.map(o => ({...o})) });
    setShowQForm(true);
  };

  const qFormChange = (key, val) => setQForm(p => ({ ...p, [key]: val }));

  const setOptionText = (i, val) => setQForm(p => ({ ...p, options: p.options.map((o, idx) => idx === i ? { ...o, text: val } : o) }));
  const setCorrect    = (i)      => setQForm(p => ({ ...p, options: p.options.map((o, idx) => ({ ...o, isCorrect: idx === i })) }));
  const addOption     = ()       => setQForm(p => ({ ...p, options: [...p.options, { text: '', isCorrect: false }] }));
  const removeOption  = (i)      => setQForm(p => ({ ...p, options: p.options.filter((_, idx) => idx !== i) }));

  const changeQType = (type) => {
    if (type === 'true_false') {
      setQForm(p => ({ ...p, type, options: [{ text: 'True', isCorrect: true }, { text: 'False', isCorrect: false }] }));
    } else if (type === 'fill_blank') {
      setQForm(p => ({ ...p, type, options: [{ text: '', isCorrect: true }] }));
    } else {
      setQForm(p => ({ ...p, type, options: DEFAULT_QUESTION.options.map(o => ({...o})) }));
    }
  };

  const saveQuestion = () => {
    if (!qForm.text.trim()) { showToast('Question text required', 'danger'); return; }
    if (qForm.type !== 'fill_blank' && !qForm.options.some(o => o.isCorrect)) { showToast('Mark at least one correct option', 'danger'); return; }
    if (editQIdx !== null) {
      setQuestions(p => p.map((q, i) => i === editQIdx ? qForm : q));
    } else {
      setQuestions(p => [...p, { ...qForm }]);
    }
    setShowQForm(false);
  };

  const removeQuestion = (i) => setQuestions(p => p.filter((_, idx) => idx !== i));

  const moveQuestion = (i, dir) => {
    const arr = [...questions];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setQuestions(arr);
  };

  // ── AI generation ───────────────────────────────────────────────────────────
  const generateAI = async () => {
    if (!aiForm.topic.trim()) { showToast('Enter a topic', 'danger'); return; }
    setAiLoading(true); setAiSuggestions([]); setAiSelected(new Set());
    try {
      // Use raw axios instance with extended timeout — DeepSeek can take 60–120s
      const res = await api.post(
        '/quiz/generate',
        { ...aiForm, subject: quizForm.subject, category: quizForm.category },
        { timeout: 180000 } // 3 min
      );
      const body = res.data;
      const qs = body?.data?.questions || body?.questions || [];
      if (qs.length === 0) {
        showToast(body?.message || 'AI returned no questions. Try a more specific topic.', 'danger');
      } else {
        setAiSuggestions(qs);
        setAiSelected(new Set(qs.map((_, i) => i)));
        showToast(`${qs.length} questions generated`);
      }
    } catch (e) {
      const msg = e.response?.data?.message
        || (e.code === 'ECONNABORTED' ? 'AI is taking too long. Try fewer questions or retry.' : null)
        || e.message
        || 'AI generation failed';
      showToast(msg, 'danger');
    }
    setAiLoading(false);
  };

  const addAiSelected = () => {
    const toAdd = aiSuggestions.filter((_, i) => aiSelected.has(i));
    setQuestions(p => [...p, ...toAdd]);
    setAiSuggestions([]); setAiSelected(new Set());
    showToast(`${toAdd.length} questions added`);
  };

  // ── CSV import ──────────────────────────────────────────────────────────────
  const parseCsv = async () => {
    try {
      const r = await postData('/quiz/import', { csvText });
      const qs = r.data?.questions || [];
      setCsvParsed(qs);
      setCsvSelected(new Set(qs.map((_, i) => i)));
      showToast(`${qs.length} questions parsed`);
    } catch (error) {
      showToast(error.response?.data?.message || 'CSV could not be parsed', 'danger');
    }
  };

  const readCsvFile = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText(ev.target.result);
    reader.readAsText(file);
  };

  const addCsvSelected = () => {
    const toAdd = csvParsed.filter((_, i) => csvSelected.has(i));
    setQuestions(p => [...p, ...toAdd]);
    setCsvParsed([]); setCsvText(''); setCsvSelected(new Set());
    showToast(`${toAdd.length} questions added`);
  };

  // ── quiz bank import ────────────────────────────────────────────────────────
  const loadBank = async () => {
    setBankLoading(true);
    try { const r = await getData('/quiz', { isBank: 'true', limit: 100 }); setBankQuizzes(r.records || r.data || []); }
    catch { setBankQuizzes([]); }
    setBankLoading(false);
  };

  useEffect(() => { if (qSource === 'bank') loadBank(); }, [qSource]);

  const addBankSelected = () => {
    const selected = [];
    bankQuizzes.forEach(bq => {
      (bq.questions || []).forEach(q => {
        if (bankSelected.has(q._id)) selected.push(q);
      });
    });
    setQuestions(p => [...p, ...selected]);
    setBankSelected(new Set());
    showToast(`${selected.length} questions added from bank`);
  };

  // ── take quiz ───────────────────────────────────────────────────────────────
  const startQuiz = async (quiz) => {
    try {
      const r = await postData(`/quiz/${quiz._id}/attempt`);
      const data = r.data || r;
      // Backend returns two shapes:
      //   new attempt:     { attempt, quiz }
      //   resuming active: the attempt doc directly
      // Detect by presence of the .attempt wrapper.
      const attemptDoc = data.attempt || data;
      const quizDoc    = data.quiz    || quiz;
      if (!attemptDoc?._id) { showToast('Could not start the quiz.', 'danger'); return; }
      setActiveQuiz(quizDoc);
      setAttempt(attemptDoc);
      setCurrentQ(0); setAnswers({}); setQuizResult(null);
      const totalSecs = (quizDoc.totalDuration || quiz.totalDuration) * 60;
      if (totalSecs > 0) setTimeLeft(totalSecs);
      setView('take');
    } catch { showToast('Failed to start quiz', 'danger'); }
  };

  useEffect(() => {
    if (view !== 'take' || timeLeft === null) return;
    if (timeLeft <= 0) { submitQuiz(); return; }
    timerRef.current = setTimeout(() => setTimeLeft(t => t - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [view, timeLeft]);

  const submitQuiz = async () => {
    clearTimeout(timerRef.current);
    setSubmitLoading(true);
    try {
      const ansArr = Object.entries(answers).map(([questionId, val]) => ({
        questionId, selectedOptionId: val.optionId || null, textAnswer: val.text || '', timeTaken: 0,
      }));
      const timeSpent = activeQuiz.totalDuration * 60 - (timeLeft || 0);
      const r = await putData(`/quiz/${activeQuiz._id}/attempt/${attempt._id}`, { answers: ansArr, timeSpent });
      setQuizResult(r.data || r);
      setView('result');
    } catch { showToast('Submit failed', 'danger'); }
    setSubmitLoading(false);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER helpers
  // ─────────────────────────────────────────────────────────────────────────
  const renderQuizCard = (q) => {
    const cat = categoryInfo(q.category);
    const totalQ = (q.questions || []).length;
    const totalM = (q.questions || []).reduce((s, x) => s + (x.marks || 1), 0);
    return (
      <div key={q._id} className="col-12 col-sm-6 col-lg-4">
        <div className="card h-100 shadow-sm border-0" style={{ borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ height: 5, background: cat.color }}></div>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <Badge cat={q.category} />
              <div className="d-flex gap-1">
                {q.isBank && <span className="badge bg-success" style={{ fontSize: 10 }}>Bank</span>}
                <span className={`badge ${q.status === 'published' ? 'bg-primary' : 'bg-secondary'}`} style={{ fontSize: 10 }}>{q.status}</span>
              </div>
            </div>
            <h6 className="fw-bold mb-1" style={{ fontSize: 15 }}>{q.title}</h6>
            {q.subject && <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>{q.subject}</p>}
            <div className="d-flex gap-3 mt-2" style={{ fontSize: 12, color: '#64748b' }}>
              <span><i className="bi bi-question-circle me-1"></i>{totalQ} Qs</span>
              <span><i className="bi bi-star me-1"></i>{totalM} marks</span>
              {q.totalDuration > 0 && <span><i className="bi bi-clock me-1"></i>{q.totalDuration}m</span>}
            </div>
          </div>
          <div className="card-footer bg-transparent border-top-0 d-flex gap-2 pt-0 pb-3 px-3">
            {q.status === 'published' && q.category !== 'scholarship' ? (
              <button className="btn btn-primary btn-sm flex-grow-1" onClick={() => startQuiz(q)}>
                <i className="bi bi-play-fill me-1"></i>Take Quiz
              </button>
            ) : q.status === 'published' ? (
              <button className="btn btn-outline-primary btn-sm flex-grow-1" type="button" disabled>
                <i className="bi bi-award me-1"></i>Published
              </button>
            ) : (
              <button className="btn btn-success btn-sm flex-grow-1" onClick={() => publishQuiz(q._id)} title="Publish this draft">
                <i className="bi bi-send me-1"></i>Publish
              </button>
            )}
            <button className="btn btn-outline-secondary btn-sm" onClick={() => openEdit(q._id)} title="Edit">
              <i className="bi bi-pencil"></i>
            </button>
            {!q.isBank && (
              <button className="btn btn-outline-success btn-sm" onClick={() => saveToBank(q._id)} title="Save to Bank">
                <i className="bi bi-archive"></i>
              </button>
            )}
            <button className="btn btn-outline-danger btn-sm" onClick={() => deleteQuiz(q._id)} title="Delete">
              <i className="bi bi-trash"></i>
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // VIEW: TAKE QUIZ
  // ─────────────────────────────────────────────────────────────────────────
  if (view === 'take' && activeQuiz) {
    const qs = activeQuiz.questions || [];
    const q  = qs[currentQ];
    const progress = ((currentQ + 1) / qs.length) * 100;
    const ans = answers[q?._id] || {};

    return (
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px' }}>
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="fw-bold mb-0">{activeQuiz.title}</h5>
            <span style={{ fontSize: 13, color: '#64748b' }}>Question {currentQ + 1} of {qs.length}</span>
          </div>
          <div className="d-flex align-items-center gap-3">
            {timeLeft !== null && (
              <div style={{ background: timeLeft < 60 ? '#fef2f2' : '#f0fdf4', color: timeLeft < 60 ? '#ef4444' : '#16a34a', padding: '4px 12px', borderRadius: 20, fontWeight: 700, fontSize: 16 }}>
                <i className="bi bi-clock me-1"></i>{formatTime(timeLeft)}
              </div>
            )}
            <button className="btn btn-outline-danger btn-sm" onClick={() => setView('list')}>Exit</button>
          </div>
        </div>

        {/* Progress */}
        <div style={{ height: 6, background: '#e2e8f0', borderRadius: 6, marginBottom: 24 }}>
          <div style={{ height: '100%', width: `${progress}%`, background: '#6366f1', borderRadius: 6, transition: 'width 0.3s' }}></div>
        </div>

        {q && (
          <div className="card border-0 shadow-sm" style={{ borderRadius: 14 }}>
            <div className="card-body p-4">
              <div className="d-flex justify-content-between mb-3">
                <span className="badge bg-secondary" style={{ fontSize: 11 }}>{q.difficulty}</span>
                <span className="badge bg-primary" style={{ fontSize: 11 }}>{q.marks} mark{q.marks !== 1 ? 's' : ''}</span>
              </div>
              <p className="fw-semibold mb-4" style={{ fontSize: 17, lineHeight: 1.6 }}>{q.text}</p>

              {/* MCQ / True-False options */}
              {(q.type === 'mcq' || q.type === 'true_false') && (
                <div className="d-flex flex-column gap-2">
                  {q.options.map(opt => {
                    const selected = ans.optionId === opt._id;
                    return (
                      <button key={opt._id} type="button"
                        onClick={() => setAnswers(p => ({ ...p, [q._id]: { optionId: opt._id } }))}
                        style={{
                          textAlign: 'left', padding: '12px 16px', borderRadius: 10, fontWeight: 500, fontSize: 15,
                          border: `2px solid ${selected ? '#6366f1' : '#e2e8f0'}`,
                          background: selected ? '#eef2ff' : '#fff',
                          color: selected ? '#4338ca' : '#374151',
                          cursor: 'pointer', transition: 'all 0.15s',
                        }}>
                        {opt.text}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Fill blank */}
              {q.type === 'fill_blank' && (
                <input className="form-control form-control-lg" placeholder="Type your answer..."
                  value={ans.text || ''}
                  onChange={e => setAnswers(p => ({ ...p, [q._id]: { text: e.target.value } }))}
                />
              )}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="d-flex justify-content-between mt-4">
          <button className="btn btn-outline-secondary" disabled={currentQ === 0} onClick={() => setCurrentQ(p => p - 1)}>
            <i className="bi bi-arrow-left me-1"></i>Previous
          </button>
          <div className="d-flex gap-2 flex-wrap justify-content-center" style={{ maxWidth: 300, overflow: 'hidden' }}>
            {qs.map((_, i) => (
              <button key={i} onClick={() => setCurrentQ(i)} style={{
                width: 32, height: 32, borderRadius: '50%', border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                background: i === currentQ ? '#6366f1' : answers[qs[i]?._id] ? '#dcfce7' : '#f1f5f9',
                color: i === currentQ ? '#fff' : '#374151',
              }}>{i + 1}</button>
            ))}
          </div>
          {currentQ < qs.length - 1 ? (
            <button className="btn btn-primary" onClick={() => setCurrentQ(p => p + 1)}>
              Next<i className="bi bi-arrow-right ms-1"></i>
            </button>
          ) : (
            <button className="btn btn-success" onClick={submitQuiz} disabled={submitLoading}>
              {submitLoading ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-check2-circle me-1"></i>}
              Submit Quiz
            </button>
          )}
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // VIEW: RESULT
  // ─────────────────────────────────────────────────────────────────────────
  if (view === 'result' && quizResult) {
    const pct  = quizResult.percentage || 0;
    const passed = quizResult.passed;
    const mode = quizResult.resultDisplay;

    return (
      <div style={{ maxWidth: 700, margin: '0 auto', padding: '24px 16px' }}>
        <div className="card border-0 shadow" style={{ borderRadius: 20, overflow: 'hidden' }}>
          {/* Result header */}
          <div style={{ background: passed ? 'linear-gradient(135deg,#16a34a,#22c55e)' : 'linear-gradient(135deg,#dc2626,#ef4444)', padding: '32px 24px', textAlign: 'center', color: '#fff' }}>
            <div style={{ fontSize: 56 }}>{passed ? '🎉' : '📚'}</div>
            <h4 className="fw-bold mt-2 mb-1">{passed ? 'Congratulations!' : 'Keep Practicing!'}</h4>
            <p style={{ opacity: 0.9, marginBottom: 0 }}>{quizResult.quizTitle}</p>
          </div>

          <div className="card-body p-4">
            {quizResult.resultReleased === false ? (
              <div className="text-center py-4">
                <i className="bi bi-clock-history" style={{ fontSize: 48, color: '#94a3b8' }}></i>
                <p className="mt-3 fw-semibold">Results will be released by your instructor.</p>
              </div>
            ) : (
              <>
                {/* Score summary */}
                <div className="row g-3 mb-4">
                  <div className="col-6 col-md-3">
                    <div className="text-center p-3 rounded-3" style={{ background: '#f8fafc' }}>
                      <div className="fw-bold" style={{ fontSize: 24, color: '#6366f1' }}>{pct}%</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>Score</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-center p-3 rounded-3" style={{ background: '#f8fafc' }}>
                      <div className="fw-bold" style={{ fontSize: 24, color: '#22c55e' }}>{quizResult.score}</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>Marks</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-center p-3 rounded-3" style={{ background: '#f8fafc' }}>
                      <div className="fw-bold" style={{ fontSize: 24, color: '#f59e0b' }}>{quizResult.totalMarks}</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>Total</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-center p-3 rounded-3" style={{ background: '#f8fafc' }}>
                      <div className="fw-bold" style={{ fontSize: 24, color: '#64748b' }}>{formatTime(quizResult.timeSpent || 0)}</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>Time</div>
                    </div>
                  </div>
                </div>

                {/* Progress bar */}
                <div style={{ marginBottom: 24 }}>
                  <div className="d-flex justify-content-between mb-1" style={{ fontSize: 13 }}>
                    <span>Score</span><span>{pct}% (Pass: {quizResult.passingScore}%)</span>
                  </div>
                  <div style={{ height: 10, background: '#e2e8f0', borderRadius: 5 }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: passed ? '#22c55e' : '#ef4444', borderRadius: 5 }}></div>
                  </div>
                </div>

                {/* Question review (marks_with_answers / with_explanation) */}
                {(mode === 'marks_with_answers' || mode === 'with_explanation') && quizResult.review && (
                  <div>
                    <h6 className="fw-bold mb-3">Question Review</h6>
                    <div className="d-flex flex-column gap-3">
                      {quizResult.review.map((r, i) => (
                        <div key={i} className="p-3 rounded-3" style={{ background: r.isCorrect ? '#f0fdf4' : '#fef2f2', border: `1px solid ${r.isCorrect ? '#bbf7d0' : '#fecaca'}` }}>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <p className="fw-semibold mb-0" style={{ fontSize: 14 }}>Q{i + 1}. {r.questionText}</p>
                            <span style={{ fontSize: 12, fontWeight: 700, color: r.isCorrect ? '#16a34a' : '#dc2626', whiteSpace: 'nowrap', marginLeft: 12 }}>
                              {r.isCorrect ? '✓' : '✗'} {r.marksAwarded} mark{r.marksAwarded !== 1 ? 's' : ''}
                            </span>
                          </div>
                          {r.options && (
                            <div className="d-flex flex-column gap-1">
                              {r.options.map(o => (
                                <div key={o._id} style={{
                                  fontSize: 13, padding: '4px 10px', borderRadius: 6,
                                  background: o.isCorrect ? '#dcfce7' : (o._id === r.selectedOptionId && !o.isCorrect ? '#fee2e2' : 'transparent'),
                                  color: o.isCorrect ? '#16a34a' : '#374151',
                                }}>
                                  {o.isCorrect ? '✓ ' : (o._id === r.selectedOptionId ? '✗ ' : '  ')}{o.text}
                                </div>
                              ))}
                            </div>
                          )}
                          {mode === 'with_explanation' && r.explanation && (
                            <div className="mt-2" style={{ fontSize: 13, color: '#475569', background: '#fff', padding: '8px 12px', borderRadius: 8, borderLeft: '3px solid #6366f1' }}>
                              <strong>Explanation:</strong> {r.explanation}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            <button className="btn btn-primary w-100 mt-4 fw-semibold" onClick={() => setView('list')}>
              <i className="bi bi-arrow-left me-2"></i>Back to Quizzes
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // VIEW: CREATE / EDIT
  // ─────────────────────────────────────────────────────────────────────────
  if (view === 'create' || view === 'edit') {
    const totalMarks = questions.reduce((s, q) => s + (q.marks || 1), 0);
    const autoTimePerQ = quizForm.totalDuration > 0 && questions.length > 0
      ? Math.floor((quizForm.totalDuration * 60) / questions.length)
      : 0;

    return (
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '16px' }}>
        <ToastNotice toast={toast} />
        {/* Top bar */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <button className="btn btn-link p-0 text-secondary" onClick={() => setView('list')}>
              <i className="bi bi-arrow-left me-1"></i>Back
            </button>
            <h5 className="fw-bold mb-0 mt-1">{view === 'edit' ? 'Edit Quiz' : 'Create Quiz'}</h5>
          </div>
          <div className="d-flex gap-2">
            <button type="button" className="btn btn-outline-primary" onClick={() => saveQuiz(false)} disabled={saveLoading}>
              {saveLoading ? <span className="spinner-border spinner-border-sm me-1"></span> : null}
              Save Draft
            </button>
            <button type="button" className="btn btn-primary" onClick={() => saveQuiz(true)} disabled={saveLoading}>
              <i className="bi bi-send me-1"></i>Publish
            </button>
          </div>
        </div>

        {/* Steps */}
        <div className="d-flex gap-2 mb-4">
          {[{ n: 1, label: 'Quiz Settings' }, { n: 2, label: `Questions (${questions.length})` }].map(s => (
            <button key={s.n} onClick={() => setCreateStep(s.n)}
              style={{
                flex: 1, padding: '10px', border: 'none', borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer',
                background: createStep === s.n ? '#6366f1' : '#f1f5f9',
                color: createStep === s.n ? '#fff' : '#64748b',
              }}>
              <span style={{ background: createStep === s.n ? 'rgba(255,255,255,0.2)' : '#e2e8f0', borderRadius: '50%', width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, marginRight: 8 }}>{s.n}</span>
              {s.label}
            </button>
          ))}
        </div>

        {/* ── STEP 1: Settings ── */}
        {createStep === 1 && (
          <div className="card border-0 shadow-sm p-4" style={{ borderRadius: 14 }}>
            <div className="row g-3">
              {/* Title */}
              <div className="col-12">
                <label className="form-label fw-semibold">Quiz Title *</label>
                <input className="form-control" placeholder="e.g., Python Basics – Chapter 1" value={quizForm.title} onChange={e => setQuizForm(p => ({ ...p, title: e.target.value }))} />
              </div>
              {/* Description */}
              <div className="col-12">
                <label className="form-label fw-semibold">Description</label>
                <textarea className="form-control" rows={2} placeholder="Brief description..." value={quizForm.description} onChange={e => setQuizForm(p => ({ ...p, description: e.target.value }))} />
              </div>
              {/* Category */}
              <div className="col-12">
                <label className="form-label fw-semibold">Category *</label>
                <div className="d-flex gap-2 flex-wrap">
                  {CATEGORIES.map(c => (
                    <button key={c.value} type="button" onClick={() => setQuizForm(p => ({
                      ...p,
                      category: c.value,
                      scholarshipConfig: {
                        ...(p.scholarshipConfig || DEFAULT_QUIZ.scholarshipConfig),
                        enabled: c.value === 'scholarship',
                      },
                    }))}
                      style={{
                        flex: 1, padding: '12px', border: `2px solid ${quizForm.category === c.value ? c.color : '#e2e8f0'}`,
                        borderRadius: 10, background: quizForm.category === c.value ? c.bg : '#fff',
                        color: quizForm.category === c.value ? c.color : '#374151', cursor: 'pointer', fontWeight: 600, fontSize: 13,
                      }}>
                      <i className={`bi ${c.icon} d-block mb-1`} style={{ fontSize: 20 }}></i>{c.label}
                    </button>
                  ))}
                </div>
              </div>
              {quizForm.category === 'scholarship' && (
                <div className="col-12">
                  <div className="border rounded-3 p-3" style={{ background: '#faf5ff', borderColor: '#ddd6fe' }}>
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <i className="bi bi-award-fill" style={{ color: '#7c3aed' }}></i>
                      <div>
                        <div className="fw-semibold">Scholarship Reward</div>
                        <small className="text-muted">The final score percentage becomes the student's discount for this course.</small>
                      </div>
                    </div>
                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">Discount Course *</label>
                        <select
                          className="form-select"
                          value={quizForm.scholarshipConfig?.targetCourseId || ''}
                          onChange={(e) => setQuizForm((previous) => ({
                            ...previous,
                            scholarshipConfig: { ...previous.scholarshipConfig, enabled: true, targetCourseId: e.target.value },
                          }))}
                        >
                          <option value="">Select course</option>
                          {courseOptions.map((course) => (
                            <option key={course._id} value={course._id}>
                              {course.courseName}{course.courseCode ? ` (${course.courseCode})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">Attempts Per Student</label>
                        <input
                          type="number"
                          min={1}
                          max={50}
                          className="form-control"
                          value={quizForm.scholarshipConfig?.maxAttempts || 1}
                          onChange={(e) => setQuizForm((previous) => ({
                            ...previous,
                            scholarshipConfig: {
                              ...previous.scholarshipConfig,
                              maxAttempts: Math.min(50, Math.max(1, Number(e.target.value || 1))),
                            },
                          }))}
                        />
                        <div className="form-text">How many times one student can attend.</div>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">Maximum Students</label>
                        <input
                          type="number"
                          min={0}
                          className="form-control"
                          value={quizForm.scholarshipConfig?.maxParticipants || 0}
                          onChange={(e) => setQuizForm((previous) => ({
                            ...previous,
                            scholarshipConfig: {
                              ...previous.scholarshipConfig,
                              maxParticipants: Math.max(0, Math.floor(Number(e.target.value || 0))),
                            },
                          }))}
                        />
                        <div className="form-text">Use 0 when any number of students can attend.</div>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">Available From</label>
                        <input
                          type="datetime-local"
                          className="form-control"
                          value={quizForm.scholarshipConfig?.availableFrom || ''}
                          onChange={(e) => setQuizForm((previous) => ({
                            ...previous,
                            scholarshipConfig: { ...previous.scholarshipConfig, availableFrom: e.target.value },
                          }))}
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">Available Until</label>
                        <input
                          type="datetime-local"
                          className="form-control"
                          value={quizForm.scholarshipConfig?.availableUntil || ''}
                          onChange={(e) => setQuizForm((previous) => ({
                            ...previous,
                            scholarshipConfig: { ...previous.scholarshipConfig, availableUntil: e.target.value },
                          }))}
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label fw-semibold">Quiz Card Image</label>
                        <input
                          type="file"
                          className="form-control"
                          accept="image/jpeg,image/png,image/webp"
                          disabled={thumbnailLoading}
                          onChange={(e) => {
                            uploadQuizThumbnail(e.target.files?.[0]);
                            e.target.value = '';
                          }}
                        />
                        <div className="form-text">Recommended: 1200 x 675 pixels, JPG/PNG/WebP, under 2 MB.</div>
                        {thumbnailLoading && <small className="text-primary d-block mt-2">Uploading thumbnail...</small>}
                        {quizForm.thumbnailUrl && (
                          <img
                            src={quizMediaUrl(quizForm.thumbnailUrl)}
                            alt="Quiz thumbnail preview"
                            className="mt-2"
                            style={{ width: 180, aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 8 }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* Subject */}
              <div className="col-md-6">
                <label className="form-label fw-semibold">Subject</label>
                <input className="form-control" placeholder="e.g., Mathematics" value={quizForm.subject} onChange={e => setQuizForm(p => ({ ...p, subject: e.target.value }))} />
              </div>
              {/* Passing score */}
              {quizForm.category !== 'scholarship' && (
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Passing Score (%)</label>
                  <input type="number" className="form-control" min={0} max={100} value={quizForm.passingScore} onChange={e => setQuizForm(p => ({ ...p, passingScore: Number(e.target.value) }))} />
                </div>
              )}
              {/* Duration */}
              <div className="col-md-6">
                <label className="form-label fw-semibold">Total Duration (minutes)</label>
                <input type="number" className="form-control" min={0} placeholder="0 = no limit" value={quizForm.totalDuration} onChange={e => setQuizForm(p => ({ ...p, totalDuration: Number(e.target.value) }))} />
                <div className="form-text">Time will be auto-split per question if set</div>
              </div>
              {/* Time per question */}
              <div className="col-md-6">
                <label className="form-label fw-semibold">Time Per Question (seconds)</label>
                <input type="number" className="form-control" min={0} placeholder={autoTimePerQ ? `Auto: ${autoTimePerQ}s` : '0 = auto'} value={quizForm.timePerQuestion} onChange={e => setQuizForm(p => ({ ...p, timePerQuestion: Number(e.target.value) }))} />
                {autoTimePerQ > 0 && <div className="form-text text-success">Auto-split: ~{autoTimePerQ}s per question</div>}
              </div>
              {/* Shuffle options */}
              <div className="col-md-6">
                <div className="form-check form-switch mt-2">
                  <input className="form-check-input" type="checkbox" checked={quizForm.shuffleQuestions} onChange={e => setQuizForm(p => ({ ...p, shuffleQuestions: e.target.checked }))} />
                  <label className="form-check-label">Shuffle Questions</label>
                </div>
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" checked={quizForm.shuffleOptions} onChange={e => setQuizForm(p => ({ ...p, shuffleOptions: e.target.checked }))} />
                  <label className="form-check-label">Shuffle Options</label>
                </div>
              </div>
              {/* Result display */}
              <div className="col-12">
                <label className="form-label fw-semibold">Result Display Mode *</label>
                <div className="row g-2">
                  {RESULT_MODES.map(m => (
                    <div key={m.value} className="col-12 col-md-6">
                      <button type="button" onClick={() => setQuizForm(p => ({ ...p, resultDisplay: m.value }))}
                        style={{
                          width: '100%', textAlign: 'left', padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                          border: `2px solid ${quizForm.resultDisplay === m.value ? '#6366f1' : '#e2e8f0'}`,
                          background: quizForm.resultDisplay === m.value ? '#eef2ff' : '#fff',
                        }}>
                        <div className="d-flex gap-2 align-items-start">
                          <i className={`bi ${m.icon} mt-1`} style={{ color: '#6366f1', fontSize: 18, flexShrink: 0 }}></i>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{m.label}</div>
                            <div style={{ fontSize: 11, color: '#94a3b8' }}>{m.desc}</div>
                          </div>
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-4 text-end">
              <button className="btn btn-primary" onClick={() => setCreateStep(2)}>
                Next: Add Questions<i className="bi bi-arrow-right ms-2"></i>
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 2: Questions ── */}
        {createStep === 2 && (
          <div>
            {/* Source tabs */}
            <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: 14, overflow: 'hidden' }}>
              <div className="d-flex" style={{ borderBottom: '1px solid #e2e8f0' }}>
                {[
                  { k: 'manual', label: 'Manual', icon: 'bi-pencil-square' },
                  { k: 'ai',     label: 'AI Generate', icon: 'bi-robot' },
                  { k: 'import', label: 'Import CSV', icon: 'bi-file-earmark-arrow-up' },
                  { k: 'bank',   label: 'Quiz Bank', icon: 'bi-archive' },
                ].map(t => (
                  <button key={t.k} onClick={() => setQSource(t.k)}
                    style={{
                      flex: 1, padding: '12px 8px', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer',
                      borderBottom: qSource === t.k ? '3px solid #6366f1' : '3px solid transparent',
                      background: qSource === t.k ? '#eef2ff' : '#fff', color: qSource === t.k ? '#6366f1' : '#64748b',
                    }}>
                    <i className={`bi ${t.icon} me-1`}></i>{t.label}
                  </button>
                ))}
              </div>

              <div className="p-4">
                {/* ── Manual ── */}
                {qSource === 'manual' && (
                  <div className="text-center">
                    <p style={{ color: '#64748b', fontSize: 14 }}>Build your question bank one question at a time.</p>
                    <button className="btn btn-primary" onClick={() => openQForm()}>
                      <i className="bi bi-plus-circle me-2"></i>Add Question
                    </button>
                  </div>
                )}

                {/* ── AI Generate ── */}
                {qSource === 'ai' && (
                  <div>
                    <div className="row g-3 mb-3">
                      <div className="col-12">
                        <label className="form-label fw-semibold">Topic *</label>
                        <input className="form-control" placeholder="e.g., Photosynthesis, Python loops, World War II..."
                          value={aiForm.topic} onChange={e => setAiForm(p => ({ ...p, topic: e.target.value }))} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-semibold">Number of Questions</label>
                        <input type="number" className="form-control" min={1} max={50} value={aiForm.numQuestions} onChange={e => setAiForm(p => ({ ...p, numQuestions: Number(e.target.value) }))} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-semibold">Difficulty</label>
                        <select className="form-select" value={aiForm.difficulty} onChange={e => setAiForm(p => ({ ...p, difficulty: e.target.value }))}>
                          {DIFFICULTIES.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-semibold">Question Type</label>
                        <select className="form-select" value={aiForm.questionType} onChange={e => setAiForm(p => ({ ...p, questionType: e.target.value }))}>
                          {Q_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>
                    </div>
                    <button className="btn btn-primary mb-4" onClick={generateAI} disabled={aiLoading}>
                      {aiLoading ? <><span className="spinner-border spinner-border-sm me-2"></span>Generating…</> : <><i className="bi bi-robot me-2"></i>Generate with AI</>}
                    </button>

                    {aiSuggestions.length > 0 && (
                      <div>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="fw-semibold">{aiSuggestions.length} questions generated</span>
                          <div className="d-flex gap-2">
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => setAiSelected(aiSuggestions.length === aiSelected.size ? new Set() : new Set(aiSuggestions.map((_, i) => i)))}>
                              {aiSelected.size === aiSuggestions.length ? 'Deselect All' : 'Select All'}
                            </button>
                            <button className="btn btn-success btn-sm" onClick={addAiSelected} disabled={aiSelected.size === 0}>
                              <i className="bi bi-plus me-1"></i>Add {aiSelected.size} Selected
                            </button>
                          </div>
                        </div>
                        <div className="d-flex flex-column gap-2" style={{ maxHeight: 360, overflowY: 'auto' }}>
                          {aiSuggestions.map((q, i) => (
                            <div key={i} onClick={() => setAiSelected(p => { const n = new Set(p); n.has(i) ? n.delete(i) : n.add(i); return n; })}
                              style={{ padding: '12px', borderRadius: 10, cursor: 'pointer', border: `2px solid ${aiSelected.has(i) ? '#6366f1' : '#e2e8f0'}`, background: aiSelected.has(i) ? '#eef2ff' : '#fff' }}>
                              <div className="d-flex justify-content-between mb-1">
                                <span style={{ fontSize: 13, fontWeight: 600 }}>Q{i + 1}</span>
                                <span className="badge bg-secondary" style={{ fontSize: 10 }}>{q.difficulty}</span>
                              </div>
                              <p style={{ fontSize: 14, margin: 0 }}>{q.text}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Import CSV ── */}
                {qSource === 'import' && (
                  <div>
                    <div className="p-3 mb-3 rounded-3" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: 13 }}>
                      <strong>CSV Format:</strong> question, optionA, optionB, optionC, optionD, correct (A/B/C/D), explanation, marks, difficulty
                      <br />
                      <code style={{ fontSize: 11 }}>What is 2+2?, 3, 4, 5, 6, B, Four is 2+2, 1, easy</code>
                      <br />
                      <small>Put text containing commas inside double quotes, for example: "HTML, CSS and JavaScript".</small>
                    </div>
                    <div className="d-flex gap-2 mb-3">
                      <button className="btn btn-outline-secondary btn-sm" onClick={() => fileInputRef.current?.click()}>
                        <i className="bi bi-upload me-1"></i>Upload CSV
                      </button>
                      <input ref={fileInputRef} type="file" accept=".csv,.txt" style={{ display: 'none' }} onChange={readCsvFile} />
                    </div>
                    <textarea className="form-control mb-3" rows={6} placeholder="Or paste CSV content here (first line = header)..."
                      value={csvText} onChange={e => setCsvText(e.target.value)} />
                    <button type="button" className="btn btn-primary btn-sm mb-4" onClick={parseCsv} disabled={!csvText.trim()}>
                      <i className="bi bi-search me-1"></i>Parse Questions
                    </button>

                    {csvParsed.length > 0 && (
                      <div>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="fw-semibold">{csvParsed.length} questions parsed</span>
                          <button type="button" className="btn btn-success btn-sm" onClick={addCsvSelected} disabled={csvSelected.size === 0}>
                            <i className="bi bi-plus me-1"></i>Add {csvSelected.size} Selected
                          </button>
                        </div>
                        <div className="d-flex flex-column gap-2" style={{ maxHeight: 300, overflowY: 'auto' }}>
                          {csvParsed.map((q, i) => (
                            <div key={i} onClick={() => setCsvSelected(p => { const n = new Set(p); n.has(i) ? n.delete(i) : n.add(i); return n; })}
                              style={{ padding: '10px 12px', borderRadius: 10, cursor: 'pointer', border: `2px solid ${csvSelected.has(i) ? '#6366f1' : '#e2e8f0'}`, background: csvSelected.has(i) ? '#eef2ff' : '#fff', fontSize: 14 }}>
                              <strong>Q{i + 1}:</strong> {q.text}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Quiz Bank ── */}
                {qSource === 'bank' && (
                  <div>
                    {bankLoading ? (
                      <div className="text-center py-4"><span className="spinner-border text-primary"></span></div>
                    ) : bankQuizzes.length === 0 ? (
                      <div className="text-center py-4 text-secondary">
                        <i className="bi bi-archive" style={{ fontSize: 36 }}></i>
                        <p className="mt-2">No quizzes in the bank yet. Save a quiz to the bank first.</p>
                      </div>
                    ) : (
                      <div>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <span className="fw-semibold">{bankQuizzes.length} quiz{bankQuizzes.length !== 1 ? 'zes' : ''} in bank — select questions to import</span>
                          <button className="btn btn-success btn-sm" onClick={addBankSelected} disabled={bankSelected.size === 0}>
                            <i className="bi bi-plus me-1"></i>Add {bankSelected.size} Selected
                          </button>
                        </div>
                        <div className="d-flex flex-column gap-3" style={{ maxHeight: 400, overflowY: 'auto' }}>
                          {bankQuizzes.map(bq => (
                            <div key={bq._id} className="border rounded-3 p-3">
                              <div className="fw-semibold mb-2"><Badge cat={bq.category} /> <span className="ms-2">{bq.title}</span></div>
                              {(bq.questions || []).map(q => (
                                <div key={q._id} onClick={() => setBankSelected(p => { const n = new Set(p); n.has(q._id) ? n.delete(q._id) : n.add(q._id); return n; })}
                                  style={{ padding: '8px 10px', borderRadius: 8, cursor: 'pointer', margin: '4px 0',
                                    border: `2px solid ${bankSelected.has(q._id) ? '#6366f1' : '#e2e8f0'}`,
                                    background: bankSelected.has(q._id) ? '#eef2ff' : '#f8fafc', fontSize: 13 }}>
                                  {q.text}
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Question list */}
            {questions.length > 0 && (
              <div className="card border-0 shadow-sm p-3" style={{ borderRadius: 14 }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-bold mb-0">{questions.length} Question{questions.length !== 1 ? 's' : ''} · {totalMarks} Total Marks</h6>
                  <button className="btn btn-outline-primary btn-sm" onClick={() => openQForm()}>
                    <i className="bi bi-plus me-1"></i>Add Manually
                  </button>
                </div>
                <div className="d-flex flex-column gap-2">
                  {questions.map((q, i) => (
                    <div key={i} className="d-flex align-items-start gap-2 p-3 rounded-3" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#6366f1', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p className="mb-1" style={{ fontSize: 14, fontWeight: 500 }}>{q.text}</p>
                        <div className="d-flex gap-2" style={{ fontSize: 11, color: '#94a3b8' }}>
                          <span>{q.type}</span>
                          <span>{q.difficulty}</span>
                          <span>{q.marks} mark{q.marks !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                      <div className="d-flex gap-1">
                        <button className="btn btn-link p-1" onClick={() => moveQuestion(i, -1)} disabled={i === 0} title="Move up"><i className="bi bi-arrow-up" style={{ fontSize: 14 }}></i></button>
                        <button className="btn btn-link p-1" onClick={() => moveQuestion(i, 1)} disabled={i === questions.length - 1} title="Move down"><i className="bi bi-arrow-down" style={{ fontSize: 14 }}></i></button>
                        <button className="btn btn-link p-1 text-primary" onClick={() => openQForm(i)} title="Edit"><i className="bi bi-pencil" style={{ fontSize: 14 }}></i></button>
                        <button className="btn btn-link p-1 text-danger" onClick={() => removeQuestion(i)} title="Remove"><i className="bi bi-trash" style={{ fontSize: 14 }}></i></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Question Form Modal ── */}
        {showQForm && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '24px 16px' }}>
            <div className="card border-0 shadow" style={{ borderRadius: 16, width: '100%', maxWidth: 640 }}>
              <div className="card-header bg-white border-bottom-0 d-flex justify-content-between align-items-center pt-4 px-4">
                <h6 className="fw-bold mb-0">{editQIdx !== null ? 'Edit Question' : 'Add Question'}</h6>
                <button className="btn-close" onClick={() => setShowQForm(false)}></button>
              </div>
              <div className="card-body px-4 pb-4">
                {/* Type */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">Question Type</label>
                  <div className="d-flex gap-2">
                    {Q_TYPES.map(t => (
                      <button key={t.value} type="button" onClick={() => changeQType(t.value)}
                        style={{ flex: 1, padding: '8px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: `2px solid ${qForm.type === t.value ? '#6366f1' : '#e2e8f0'}`, background: qForm.type === t.value ? '#eef2ff' : '#fff', color: qForm.type === t.value ? '#4338ca' : '#374151' }}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Question text */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">Question Text *</label>
                  <textarea className="form-control" rows={3} placeholder="Enter question..." value={qForm.text} onChange={e => qFormChange('text', e.target.value)} />
                </div>
                {/* Options */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">{qForm.type === 'fill_blank' ? 'Correct Answer' : 'Options'}</label>
                  <div className="d-flex flex-column gap-2">
                    {qForm.options.map((opt, i) => (
                      <div key={i} className="d-flex gap-2 align-items-center">
                        {qForm.type !== 'fill_blank' && (
                          <input type="radio" name="correct" checked={opt.isCorrect} onChange={() => setCorrect(i)} title="Mark as correct" style={{ marginTop: 0, cursor: 'pointer' }} />
                        )}
                        <input className="form-control form-control-sm" placeholder={qForm.type === 'fill_blank' ? 'Correct answer...' : `Option ${String.fromCharCode(65 + i)}`}
                          value={opt.text} onChange={e => setOptionText(i, e.target.value)} />
                        {qForm.type === 'mcq' && qForm.options.length > 2 && (
                          <button type="button" className="btn btn-link text-danger p-0" onClick={() => removeOption(i)}><i className="bi bi-x-circle"></i></button>
                        )}
                      </div>
                    ))}
                  </div>
                  {qForm.type === 'mcq' && qForm.options.length < 6 && (
                    <button type="button" className="btn btn-link btn-sm mt-1 p-0" onClick={addOption}>
                      <i className="bi bi-plus me-1"></i>Add Option
                    </button>
                  )}
                </div>
                {/* Explanation */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">Explanation <span className="text-muted fw-normal">(optional)</span></label>
                  <textarea className="form-control" rows={2} placeholder="Explain the correct answer..." value={qForm.explanation} onChange={e => qFormChange('explanation', e.target.value)} />
                </div>
                {/* Marks & Difficulty */}
                <div className="row g-2 mb-4">
                  <div className="col-6">
                    <label className="form-label fw-semibold">Marks</label>
                    <input type="number" className="form-control" min={0.5} step={0.5} value={qForm.marks} onChange={e => qFormChange('marks', Number(e.target.value))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-semibold">Difficulty</label>
                    <select className="form-select" value={qForm.difficulty} onChange={e => qFormChange('difficulty', e.target.value)}>
                      {DIFFICULTIES.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
                    </select>
                  </div>
                </div>
                <div className="d-flex gap-2">
                  <button className="btn btn-outline-secondary flex-grow-1" onClick={() => setShowQForm(false)}>Cancel</button>
                  <button className="btn btn-primary flex-grow-1" onClick={saveQuestion}>
                    {editQIdx !== null ? 'Update Question' : 'Add Question'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // VIEW: LIST
  // ─────────────────────────────────────────────────────────────────────────
  const TABS = [
    { k: 'all', label: 'All Quizzes', icon: 'bi-grid' },
    { k: 'practice', label: 'Practice', icon: 'bi-pencil' },
    { k: 'test', label: 'Test', icon: 'bi-clipboard-check' },
    { k: 'exam', label: 'Exam', icon: 'bi-mortarboard' },
    { k: 'bank', label: 'Quiz Bank', icon: 'bi-archive' },
    { k: 'policy', label: 'Policy / Manual', icon: 'bi-journal-text' },
  ];

  return (
    <div style={{ padding: '16px' }}>
      {/* Toast */}
      <ToastNotice toast={toast} />

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h4 className="fw-bold mb-0"><i className="bi bi-patch-question-fill me-2 text-primary"></i>Quiz Generator</h4>
          <p className="text-secondary mb-0" style={{ fontSize: 13 }}>Create, manage, and conduct quizzes with AI assistance</p>
        </div>
        <button className="btn btn-primary fw-semibold" onClick={openCreate}>
          <i className="bi bi-plus-circle me-2"></i>Create Quiz
        </button>
      </div>

      {/* Tabs + Search */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex gap-1 flex-wrap">
          {TABS.map(t => (
            <button key={t.k} onClick={() => setActiveTab(t.k)}
              style={{
                padding: '7px 14px', border: 'none', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                background: activeTab === t.k ? '#6366f1' : '#f1f5f9',
                color: activeTab === t.k ? '#fff' : '#64748b',
              }}>
              <i className={`bi ${t.icon} me-1`}></i>{t.label}
            </button>
          ))}
        </div>
        <input className="form-control" style={{ maxWidth: 220, borderRadius: 20 }} placeholder="Search quizzes..."
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* Policy tab */}
      {activeTab === 'policy' ? (
        <PolicyTab moduleKey="quiz-generator" label="Quiz Generation Policy / Manual" />
      ) : /* Quiz grid */
      loading ? (
        <div className="text-center py-5"><span className="spinner-border text-primary"></span></div>
      ) : quizzes.length === 0 ? (
        <div className="text-center py-5 text-secondary">
          <i className={`bi ${activeTab === 'bank' ? 'bi-archive' : 'bi-patch-question'}`} style={{ fontSize: 52 }}></i>
          {activeTab === 'bank' ? (
            <>
              <p className="mt-3 fw-semibold">Quiz Bank is empty</p>
              <p style={{ fontSize: 13, maxWidth: 460, margin: '0 auto 12px' }}>
                The Quiz Bank is a reusable library — separate from drafts. Open any quiz from
                <strong> All Quizzes</strong> and click the <i className="bi bi-archive mx-1"></i>
                <strong>Save to Bank</strong> icon to add it here.
              </p>
              <button className="btn btn-outline-primary mt-2" onClick={() => setActiveTab('all')}>
                <i className="bi bi-arrow-left me-2"></i>Back to All Quizzes
              </button>
            </>
          ) : (
            <>
              <p className="mt-3 fw-semibold">No quizzes yet</p>
              <button className="btn btn-primary mt-2" onClick={openCreate}>
                <i className="bi bi-plus me-2"></i>Create Your First Quiz
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="row g-3">
          {quizzes.map(q => renderQuizCard(q))}
        </div>
      )}
    </div>
  );
}
